import { RebatePoint, RebateBreakdown } from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';

interface RebateRuleTier {
  tierOrder: number;
  thresholdValue: string;
}

export interface RebateBreakdownCalculation {
  conditionalRebateYtd: number;
  nonConditionalRebateYtd: number;
  totalRebateYtd: number;
}

export const formatThresholdRange = (
  tiers: RebateRuleTier[],
  tierOrder: number,
  thresholdType: string | null,
  growthBaseline?: string | number,
) => {
  if (!thresholdType) return '';

  const currentTier = tiers.find((t) => t.tierOrder === tierOrder);
  if (!currentTier) return '';

  // Sort tiers by tierOrder to ensure proper ordering
  const sortedTiers = [...tiers].sort((a, b) => a.tierOrder - b.tierOrder);

  const currentTierIndex = sortedTiers.findIndex(
    (t) => t.tierOrder === tierOrder,
  );
  const currentTierThresholdValue = parseFloat(currentTier.thresholdValue);

  // Check if there's a next tier
  const nextTier = sortedTiers[currentTierIndex + 1];

  if (nextTier) {
    // Has next tier: show range from current threshold to next threshold - $0.01
    const nextTierThresholdValue = parseFloat(nextTier.thresholdValue);
    if (thresholdType === 'growth') {
      // For growth thresholds, value is absolute percentage of baseline
      // e.g., 105 = 105% of baseline, 90 = 90% of baseline
      const baseline = parseFloat(growthBaseline?.toString() || '0');
      if (baseline > 0) {
        const currentDollarThreshold =
          baseline * (currentTierThresholdValue / 100);
        const nextDollarThreshold =
          baseline * (nextTierThresholdValue / 100) - 0.01;
        return `${currentTierThresholdValue.toFixed(0)}% - ${(
          nextTierThresholdValue - 0.01
        ).toFixed(2)}% (${formatCurrency(
          currentDollarThreshold,
        )} - ${formatCurrency(nextDollarThreshold)})`;
      } else {
        return `${currentTierThresholdValue.toFixed(0)}% - ${(
          nextTierThresholdValue - 0.01
        ).toFixed(2)}% (baseline needed)`;
      }
    } else {
      // For currency, subtract $0.01 to show the boundary clearly
      return `${formatCurrency(currentTierThresholdValue)} - ${formatCurrency(
        nextTierThresholdValue - 0.01,
      )}`;
    }
  } else {
    // No next tier: show current threshold+
    if (thresholdType === 'growth') {
      // For growth thresholds, value is absolute percentage of baseline
      const baseline = parseFloat(growthBaseline?.toString() || '0');
      if (baseline > 0) {
        const currentDollarThreshold =
          baseline * (currentTierThresholdValue / 100);
        return `${currentTierThresholdValue.toFixed(0)}%+ (${formatCurrency(
          currentDollarThreshold,
        )}+)`;
      } else {
        return `${currentTierThresholdValue.toFixed(0)}%+ (baseline needed)`;
      }
    } else {
      return `${formatCurrency(currentTierThresholdValue)}+`;
    }
  }
};

/**
 * Derives cumulative values from point-in-time estimates
 * Backend now sends point-in-time (month-by-month) values, but status cards need cumulative (YTD) values
 */
export const deriveCumulativePosition = (
  estimates: RebatePoint[],
): RebatePoint | null => {
  if (!estimates || estimates.length === 0) {
    return null;
  }

  // Get the most recent estimate (for date and metadata)
  const sortedEstimates = [...estimates].sort(
    (a, b) => a.daysFromStart - b.daysFromStart,
  );
  const lastEstimate = sortedEstimates[sortedEstimates.length - 1];

  // Sum all point-in-time values to get cumulative total
  const cumulativeTotal = estimates.reduce(
    (sum, estimate) => sum + estimate.totalRebateAmount,
    0,
  );

  // Aggregate breakdowns by rule+division to get cumulative amounts
  // Use a map to track cumulative values per rule+division
  const cumulativeBreakdownsMap = new Map<string, RebateBreakdown>();

  estimates.forEach((estimate) => {
    estimate.breakdowns.forEach((breakdown) => {
      const key = `${breakdown.ruleId}-${breakdown.divisionId}`;
      const existing = cumulativeBreakdownsMap.get(key);

      if (existing) {
        // Add to existing cumulative amount, preserving all other properties
        existing.rebateAmount += breakdown.rebateAmount;
      } else {
        // Create new entry, cloning the breakdown to preserve all properties
        cumulativeBreakdownsMap.set(key, {
          ...breakdown,
          rebateAmount: breakdown.rebateAmount,
        });
      }
    });
  });

  return {
    ...lastEstimate,
    totalRebateAmount: cumulativeTotal,
    breakdowns: Array.from(cumulativeBreakdownsMap.values()),
  };
};

/**
 * Calculates conditional vs non-conditional rebate amounts from current actual position
 * Conditional rebates = Growth rules only
 * Non-conditional rebates = Volume rules + Manual rules
 *
 * @param currentActualPosition - The current actual position with breakdowns
 * @param rebateRules - Array of rebate rules to categorize by thresholdType
 * @param isCumulative - Whether to use cumulative values (true) or incremental values (false)
 */
export const calculateConditionalVsNonConditionalRebates = (
  currentActualPosition: {
    totalRebateAmount?: number;
    totalCumulativeRebateAmount?: number;
    breakdowns?: Array<{
      ruleId: string;
      rebateAmount: number;
      cumulativeRebateAmount?: number;
    }>;
  } | null,
  rebateRules: Array<{
    id: string;
    isConditional: boolean;
  }> = [],
  isCumulative: boolean = true,
): RebateBreakdownCalculation => {
  // If no breakdowns or no rules, return zeros
  if (
    !currentActualPosition?.breakdowns ||
    currentActualPosition.breakdowns.length === 0 ||
    !rebateRules.length
  ) {
    // Use the appropriate total based on isCumulative
    const totalRebateYtd = isCumulative
      ? currentActualPosition?.totalCumulativeRebateAmount ||
        currentActualPosition?.totalRebateAmount ||
        0
      : currentActualPosition?.totalRebateAmount || 0;

    return {
      conditionalRebateYtd: 0,
      nonConditionalRebateYtd: 0,
      totalRebateYtd,
    };
  }

  // Create a map of rule ID to rule for quick lookup
  const ruleMap = new Map(rebateRules.map((rule) => [rule.id, rule]));

  let conditionalRebateYtd = 0;
  let nonConditionalRebateYtd = 0;

  // Categorize each rule's rebate amount
  currentActualPosition.breakdowns.forEach(
    ({ ruleId, rebateAmount, cumulativeRebateAmount }) => {
      // Select the appropriate amount based on isCumulative
      const amount = isCumulative
        ? (cumulativeRebateAmount ?? rebateAmount)
        : rebateAmount;

      // Skip if amount is 0 or invalid
      if (!amount || amount === 0) {
        return;
      }

      const rule = ruleMap.get(ruleId);
      if (!rule) {
        // If rule not found, default to non-conditional
        nonConditionalRebateYtd += amount;
        return;
      }

      // Use pre-computed isConditional from backend
      if (rule.isConditional) {
        conditionalRebateYtd += amount;
      } else {
        // Non-conditional rules (flat volume, manual, etc.)
        nonConditionalRebateYtd += amount;
      }
    },
  );

  return {
    conditionalRebateYtd,
    nonConditionalRebateYtd,
    totalRebateYtd: conditionalRebateYtd + nonConditionalRebateYtd,
  };
};
